import styled from '@emotion/styled'
import {css} from '@emotion/react'
import {theme} from '../../theme'

// Size variants
// Named presets. If `width` prop is passed directly it takes precedence.

const sizeMap = {
  sm: '400px',
  md: '560px',
  lg: '720px',
  xl: '960px',
  full: '100%',
}

// Backdrop

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: ${({align}) =>
    align === 'top'
      ? 'flex-start'
      : align === 'bottom'
      ? 'flex-end'
      : 'center'};
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  padding: ${({align}) =>
    align === 'top'
      ? `${theme.space[10]} ${theme.space[4]} ${theme.space[4]}`
      : align === 'bottom'
      ? `${theme.space[4]} ${theme.space[4]} ${theme.space[10]}`
      : theme.space[4]};
  animation: backdropIn ${theme.transitions.normal} forwards;

  @keyframes backdropIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

// Dialog
// Resolves width in this order:
//   1. `width` prop  — any valid CSS string, e.g. "800px", "75vw", "min(900px, 90%)"
//   2. `size` prop   — named preset key from sizeMap
//   3. fallback      — sizeMap.md

export const Dialog = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({size, width}) => width ?? sizeMap[size] ?? sizeMap.md};
  max-height: calc(100vh - ${theme.space[8]});
  background-color: ${theme.colors.bg.primary};
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.lg};
  overflow: hidden;
  animation: dialogIn ${theme.transitions.slow} cubic-bezier(0.22, 1, 0.36, 1)
    forwards;

  @keyframes dialogIn {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`

// Header

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.space[4]};
  padding: ${theme.space[6]};
  border-bottom: ${({divider}) =>
    divider ? `1px solid ${theme.colors.border.default}` : 'none'};
  flex-shrink: 0;
`

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  min-width: 0;
`

export const Title = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const Subtitle = styled.p`
  margin: 0;
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`

// Close button

export const CloseButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid ${theme.colors.border.default};
  border-radius: ${theme.radii.md};
  background-color: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast},
    border-color ${theme.transitions.fast}, color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.bg.tertiary};
    border-color: ${theme.colors.border.strong};
    color: ${theme.colors.text.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }

  svg {
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`

// Body

export const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: ${({noPadding}) => (noPadding ? '0' : `${theme.space[6]}`)};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.primary};
  line-height: 1.6;

  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.border.strong} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.border.strong};
    border-radius: ${theme.radii.full};
  }
`

// Footer

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({align}) =>
    align === 'left'
      ? 'flex-start'
      : align === 'center'
      ? 'center'
      : 'flex-end'};
  gap: ${theme.space[3]};
  padding: ${theme.space[4]} ${theme.space[6]};
  border-top: ${({divider}) =>
    divider ? `1px solid ${theme.colors.border.default}` : 'none'};
  background-color: ${theme.colors.bg.secondary};
  flex-shrink: 0;
`

// Status banner

const statusStyles = {
  info: css`
    background-color: ${theme.colors.status.info.bg};
    border-bottom: 1px solid ${theme.colors.status.info.border};
    color: ${theme.colors.status.info.text};
  `,
  success: css`
    background-color: ${theme.colors.status.success.bg};
    border-bottom: 1px solid ${theme.colors.status.success.border};
    color: ${theme.colors.status.success.text};
  `,
  warning: css`
    background-color: ${theme.colors.status.warning.bg};
    border-bottom: 1px solid ${theme.colors.status.warning.border};
    color: ${theme.colors.status.warning.text};
  `,
  danger: css`
    background-color: ${theme.colors.status.danger.bg};
    border-bottom: 1px solid ${theme.colors.status.danger.border};
    color: ${theme.colors.status.danger.text};
  `,
}

export const StatusBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[2]};
  padding: ${theme.space[3]} ${theme.space[6]};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  flex-shrink: 0;
  ${({variant}) => statusStyles[variant] ?? statusStyles.info}
`
